/**
 * Detailansicht eines Nachweises auf der Seite "Nachweise".
 *
 * Die Überschrift mit Pfad („Vollgenehmigung › Nachweise", Nachweise führt zurück) und der
 * Bezeichnung setzt die Seite über `PageTemplate`. Hier oben ohne Kachel der Kopfbereich wie in
 * der Übersicht:
 * links der Statusstrahl, rechts hinter einer Linie der nächste Schritt mit dem Menü der übrigen
 * Aktionen. Darunter je eine Kachel für Nachweis, Termine, Kosten und Historie.
 *
 * Gespeichert wird ohne Knopf, wie überall in der Anwendung (ADR 004): Textfelder beim
 * Verlassen, Auswahlfelder mit der Änderung, Daten beim Verlassen bzw. mit der Auswahl im
 * Kalender. Ob sich ein Wert geändert hat, prüft der Hook; hier steht nur die Plausibilität.
 *
 * Die Datumsfelder teilen sich nach ihrer Art (`is_deadline` der Termin-Regel): Ereignisse
 * („Angefragt am“) und Angaben zum Dokument stehen in der Kachel „Nachweis“, Fristen
 * („Lieferung bis“) als Zeilen in der Kachel „Termine“ neben den manuellen Terminen. Dort zeigt
 * die Tabelle zuerst nur, was im aktuellen Status zählt; Fristen früherer Status klappen auf.
 * Ein Klick auf eine Frist öffnet ihr Datum in einem kleinen Dialog. Ausstellung und Gültigkeit
 * gibt es erst ab „Geliefert“ – vorher gibt es kein Dokument.
 *
 * Der Status selbst ist hier nicht editierbar (ADR 007): Er wechselt über die Aktionen, die der
 * Server anbietet. Die Termine bleiben direkt änderbar; Änderungen landen in der Historie.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Grid } from "@mui/material";
import { format } from "date-fns";
import Button from "../../../common/inputs/Button";
import Autocomplete from "../../../common/inputs/Autocomplete";
import InputField from "../../../common/inputs/InputField";
import DokumentListe from "../../../common/inputs/DokumentListe";
import InputFieldDate from "../../../common/inputs/InputFieldDate";
import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import Dropdown from "../../../common/inputs/Dropdown";
import MultiSelectDropdown, {
  MultiSelectOption,
} from "../../../common/inputs/MultiSelectDropdown";
import InputFieldMultiline from "../../../common/inputs/InputFieldMultiline";
import Section from "../../../layout/Section";
import DialogTemplate from "../../../layout/DialogTemplate";
import SeitenKopf from "../../../layout/SeitenKopf";
import EvidenceHistorie from "./EvidenceHistorie";
import NachweisStatusStrahl from "./NachweisStatusStrahl";
import { hauptAktion } from "./useEvidenceAktionen";
import { ActionsMenuButton } from "../../../common/overlays/ActionsMenu";
import { bausteinTypIds } from "./bausteinOptionen";
import {
  suggestedEmail,
  useEvidenceExperts,
} from "../hooks/useEvidenceExperts";
import { alsBetrag, betragAlsFeldwert } from "./betrag";
import { CostAccess } from "./EvidenceUebergangDialog";

import {
  DeadlineItem,
  Dokument,
  Evidence,
  EvidenceAktion,
  EvidenceHistorieEintrag,
  EvidenceTerminfeld,
  LookupOption,
  EvidenceUpdate,
  PermitCostItem,
  ProcessingPath,
} from "../../../../types";
import { OrgUser } from "../../../../services/api/users";
import { processingPathTooltip } from "./processingPathTooltip";

/** Alle Datumsfelder des Nachweises – welche davon das Detail zeigt, sagt der Server */
const DATUMSFELDER = [
  "completed_at",
  "requested_at",
  "angefragt_am",
  "erinnerung_am",
  "angebot_erhalten_am",
  "beauftragung_erinnerung_am",
  "offer_valid_until",
  "beauftragt_am",
  "lieferung_bis",
  "geliefert_am",
  "pruefung_bis",
  "freigegeben_am",
  "report_valid_until",
] as const;
type Datumsfeld = (typeof DATUMSFELDER)[number];

type Textfeld =
  | "bezeichnung"
  | "gutachter"
  | "expert_email"
  | "institution"
  | "requested_from_email"
  | "note";

/** Lokaler Stand der Felder, in die man tippt – gespeichert wird erst beim Verlassen */
type Entwurf = Record<Textfeld | Datumsfeld, string> & {
  dokumente: Dokument[];
};

const ausNachweis = (evidence: Evidence): Entwurf => ({
  bezeichnung: evidence.bezeichnung ?? "",
  gutachter: evidence.gutachter ?? "",
  expert_email: evidence.expert_email ?? "",
  institution: evidence.institution ?? "",
  requested_from_email: evidence.requested_from_email ?? "",
  note: evidence.note ?? "",
  dokumente: evidence.dokumente ?? [],
  ...(Object.fromEntries(
    DATUMSFELDER.map((feld) => [feld, evidence[feld] ?? ""]),
  ) as Record<Datumsfeld, string>),
});

const heute = (): string => format(new Date(), "yyyy-MM-dd");

/**
 * Prüft ein Datumsfeld nach den Bedingungen, die der Server mitliefert – derselbe Wortlaut, den
 * er bei einem Verstoß selbst meldet. Die Bedingungen selbst stehen nur in der Regel.
 */
const terminFehler = (
  feld: EvidenceTerminfeld,
  werte: Entwurf,
): string | undefined => {
  const wert = werte[feld.name as Datumsfeld];
  if (!wert) return undefined;
  if (feld.ereignis && wert > heute()) {
    return "Darf nicht in der Zukunft liegen";
  }
  const frueher = feld.nicht_vor
    ? werte[feld.nicht_vor as Datumsfeld]
    : undefined;
  if (frueher && wert < frueher) {
    return `Muss auf oder nach „${feld.nicht_vor_label}“ liegen`;
  }
  return undefined;
};

const REQUESTER_SIDES = [
  { value: "internal", label: "Intern" },
  { value: "external", label: "Extern" },
];

interface EvidenceDetailProps {
  evidence: Evidence;
  orgUsers: OrgUser[];
  evidenceTypeOptions: LookupOption[];
  /** Antragsbausteine zur Wahl – Vorschläge oben, dann der ganze Katalog (`bausteinOptionen`) */
  bausteinOptionen: MultiSelectOption[];
  canEdit: boolean;
  /** Speichert geänderte Felder; meldet Erfolg und Fehler selbst über PageTemplate */
  onSave: (evidenceId: string, data: EvidenceUpdate) => Promise<void>;
  /** Kostenposition dieses Nachweises, falls schon eine angelegt ist */
  costItem?: PermitCostItem;
  /** Recht an „Genehmigung › Kosten“: ohne Leserecht keine Kachel, ohne Schreibrecht nur lesend */
  costAccess: CostAccess;
  /**
   * Speichert einen Betrag. Die Kostenposition ist ein eigenes Objekt; beim ersten Betrag legt
   * der Aufrufer sie still an.
   */
  onCostSave: (data: {
    betrag_geplant?: number | null;
    betrag_ist?: number | null;
  }) => Promise<void>;
  /** Öffnet den Dialog zu einer Aktion (Statusübergang, Archivieren) */
  onOeffneAktion: (aktion: EvidenceAktion) => void;
  /** Wechselt den Bearbeitungsweg – aus „Offen“ sofort, sonst nach Rückfrage */
  onChangePath: (path: ProcessingPath) => void;
  /** Öffnet das Menü aller zulässigen Aktionen an diesem Element */
  onOeffneMenu: (anchor: HTMLElement) => void;
  ladeHistorie: (evidence: Evidence) => Promise<EvidenceHistorieEintrag[]>;
  /**
   * Kachel „Termine" des Nachweises – baut die Seite, die die manuellen Termine lädt. Die
   * Fristen-Felder des Nachweises liefert das Detail mit; ein Klick darauf öffnet ihr Datum hier.
   */
  renderDeadlinesSection?: (fields: {
    fieldRows: { current: DeadlineItem[]; earlier: DeadlineItem[] };
    onFieldRowClick: (deadline: DeadlineItem) => void;
  }) => React.ReactNode;
  /**
   * Ändert sich mit den Terminen des Nachweises: Anlegen, Abhaken und Löschen stehen in der
   * Historie, die sonst nur bei einer Änderung am Nachweis selbst neu lädt.
   */
  historyKey?: string;
}

const EvidenceDetail: React.FC<EvidenceDetailProps> = ({
  evidence,
  orgUsers,
  evidenceTypeOptions,
  bausteinOptionen,
  canEdit,
  onSave,
  costItem,
  costAccess,
  onCostSave,
  onOeffneAktion,
  onChangePath,
  onOeffneMenu,
  ladeHistorie,
  renderDeadlinesSection,
  historyKey,
}) => {
  // Vorschläge nur, wenn man das Feld auch ändern kann – neu geladen beim Wechsel ins Bearbeiten
  const isCommission = evidence.processing_path === "commission";
  const isRequest = evidence.processing_path === "request";
  const experts = useEvidenceExperts(canEdit && isCommission);
  const institutions = useEvidenceExperts(canEdit && isRequest, "institutions");
  // Intern oder extern – solange niemand eingetragen ist, gilt die Auswahl nur hier.
  const [side, setSide] = useState<"internal" | "external">(
    evidence.requested_from_user_id ? "internal" : "external",
  );
  useEffect(() => {
    if (evidence.requested_from_user_id) setSide("internal");
    else if (evidence.institution) setSide("external");
  }, [evidence.requested_from_user_id, evidence.institution]);
  const serverstand = useMemo(() => ausNachweis(evidence), [evidence]);
  const [entwurf, setEntwurf] = useState<Entwurf>(serverstand);
  const [fehler, setFehler] = useState<Partial<Record<Datumsfeld, string>>>({});

  // Kommt der Nachweis vom Server zurück, übernimmt der Entwurf nur die Felder, die sich dort
  // geändert haben. So setzt die Antwort auf ein gespeichertes Feld nicht den Text zurück, den
  // man gerade in ein anderes tippt. Ein anderer Nachweis füllt alles neu.
  const bisherigeId = useRef(evidence.id);
  const bisherigerServerstand = useRef(serverstand);
  useEffect(() => {
    const vorher = bisherigerServerstand.current;
    bisherigerServerstand.current = serverstand;
    if (bisherigeId.current !== evidence.id) {
      bisherigeId.current = evidence.id;
      setEntwurf(serverstand);
      setFehler({});
      return;
    }
    setEntwurf((prev) => {
      const next = { ...prev };
      (Object.keys(serverstand) as (keyof Entwurf)[]).forEach((feld) => {
        if (
          JSON.stringify(serverstand[feld]) !== JSON.stringify(vorher[feld])
        ) {
          (next as Record<string, unknown>)[feld] = serverstand[feld];
        }
      });
      return next;
    });
  }, [evidence.id, serverstand]);

  const speichern = useCallback(
    (data: EvidenceUpdate) => {
      if (canEdit) void onSave(evidence.id, data);
    },
    [canEdit, onSave, evidence.id],
  );

  // Die Auswahl hält der Detail-Abschnitt, solange die Liste offen ist; kommt der Nachweis vom
  // Server zurück, gilt wieder dessen Zuordnung.
  const [bausteine, setBausteine] = useState<string[]>(() =>
    bausteinTypIds(evidence),
  );
  useEffect(() => {
    setBausteine(bausteinTypIds(evidence));
  }, [evidence]);

  const setze = <K extends keyof Entwurf>(feld: K, wert: Entwurf[K]) =>
    setEntwurf((prev) => ({ ...prev, [feld]: wert }));

  const evidenceTypeDropdownOptions = useMemo(
    () => evidenceTypeOptions.map((t) => ({ value: t.id, label: t.name })),
    [evidenceTypeOptions],
  );

  /**
   * Die Auswahl sind die Mitglieder der Organisation – Quelle ist Auth0, nicht unsere
   * Benutzertabelle. Beides kann auseinanderlaufen: Wer den Nachweis einmal übernommen hat
   * und danach aus der Auth0-Organisation fällt, steht weiter am Nachweis, aber nicht mehr
   * in der Liste. Ohne passenden Eintrag zeigt das Auswahlfeld nichts an, obwohl die Tabelle
   * daneben einen Namen führt. Deshalb wird der hinterlegte Verantwortliche ergänzt, wenn
   * die Liste ihn nicht kennt.
   */
  const userOptions = useMemo(() => {
    const options = orgUsers.map((u) => ({
      value: u.user_id,
      label: u.name || u.email,
    }));
    [evidence.verantwortlich, evidence.requested_from_user].forEach(
      (hinterlegt) => {
        const id = hinterlegt?.external_id;
        if (id && !options.some((o) => o.value === id)) {
          options.push({
            value: id,
            label: hinterlegt.display_name || hinterlegt.username || id,
          });
        }
      },
    );
    return options;
  }, [orgUsers, evidence.verantwortlich, evidence.requested_from_user]);

  const pathOptions = evidence.path_options.map((o) => ({
    value: o.value,
    label: o.label,
    tooltip: processingPathTooltip(o),
  }));
  const currentPath = evidence.path_options.find(
    (o) => o.value === evidence.processing_path,
  );

  /**
   * Übernimmt beim Wählen eines bekannten Gutachters bzw. einer bekannten Institution deren
   * letzte E-Mail-Adresse – nur in ein leeres Feld, eine eingetragene Adresse bleibt.
   */
  const saveNameWithEmail = (
    name: "gutachter" | "institution",
    email: "expert_email" | "requested_from_email",
    wert: string,
  ) => {
    const vorschlag = suggestedEmail(
      name === "gutachter" ? experts : institutions,
      wert,
    );
    const neueEmail = !entwurf[email] && vorschlag ? vorschlag : undefined;
    if (neueEmail) setze(email, neueEmail);
    speichern({
      [name]: wert.trim() || null,
      ...(neueEmail ? { [email]: neueEmail } : {}),
    } as EvidenceUpdate);
  };

  const knopfAktion = hauptAktion(evidence);
  // Welche Termine aktuell sind und welche zu früheren Status gehören, sagt der Server (Regel
  // `regeln/evidence_status.termine_zum_status`).
  const aktuelleTermine = evidence.terminfelder.filter((feld) => feld.aktuell);
  const fruehereTermine = evidence.terminfelder.filter((feld) => !feld.aktuell);
  // Ereignisse und Dokumentangaben in der Kachel „Nachweis“, in der Reihenfolge des Ablaufs.
  const angaben = [...fruehereTermine, ...aktuelleTermine].filter(
    (feld) => !feld.is_deadline,
  );

  /** Eine Frist des Nachweises als Zeile der Termin-Tabelle */
  const alsTerminzeile = (feld: EvidenceTerminfeld): DeadlineItem => ({
    id: `field:${feld.name}`,
    bezeichnung: feld.label,
    zieltermin: entwurf[feld.name as Datumsfeld] || undefined,
    verantwortlich: evidence.verantwortlich,
    source: "derived",
    reference_type: "evidence",
    permit_procedure_id: evidence.permit_procedure_id,
    evidence_id: evidence.id,
  });
  const fieldRows = {
    current: aktuelleTermine.filter((f) => f.is_deadline).map(alsTerminzeile),
    // Frühere Fristen nur, wenn sie gesetzt waren – eine leere frühere Frist sagt nichts.
    earlier: fruehereTermine
      .filter((f) => f.is_deadline && entwurf[f.name as Datumsfeld])
      .map(alsTerminzeile),
  };

  /** Frist im Dialog: das Feld und der Wert, solange er nicht gespeichert ist */
  const [offeneFrist, setOffeneFrist] = useState<{
    feld: EvidenceTerminfeld;
    wert: string;
    fehler?: string;
  } | null>(null);

  const oeffneFrist = (zeile: DeadlineItem) => {
    if (!canEdit) return;
    const feld = evidence.terminfelder.find(
      (f) => `field:${f.name}` === zeile.id,
    );
    if (feld) setOffeneFrist({ feld, wert: entwurf[feld.name as Datumsfeld] });
  };

  const fristSpeichern = () => {
    if (!offeneFrist) return;
    const { feld, wert } = offeneFrist;
    const name = feld.name as Datumsfeld;
    const gefunden = terminFehler(feld, { ...entwurf, [name]: wert });
    if (gefunden) {
      setOffeneFrist({ ...offeneFrist, fehler: gefunden });
      return;
    }
    setze(name, wert);
    speichern({ [name]: wert || null } as EvidenceUpdate);
    setOffeneFrist(null);
  };

  /**
   * Abschluss einer Datumseingabe: prüfen, dann speichern. Folgetermine (die Angebotsfrist nach
   * der Anfrage) setzt der Server und schickt sie mit der Antwort zurück.
   */
  const datumSpeichern = (feld: EvidenceTerminfeld, wert: string | null) => {
    const name = feld.name as Datumsfeld;
    const werte = { ...entwurf, [name]: wert ?? "" };
    const gefunden = terminFehler(feld, werte);
    setFehler((prev) => ({ ...prev, [name]: gefunden }));
    if (gefunden) return;
    speichern({ [name]: wert } as EvidenceUpdate);
  };

  const datumsfeld = (feld: EvidenceTerminfeld) => (
    <InputFieldDate
      label={feld.label}
      value={entwurf[feld.name as Datumsfeld]}
      onChange={(wert) => setze(feld.name as Datumsfeld, wert ?? "")}
      onCommit={(wert) => datumSpeichern(feld, wert)}
      readOnly={!canEdit}
      variant="floating"
      size="small"
      dateFormat="dd.MM.yyyy"
      error={fehler[feld.name as Datumsfeld]}
    />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Kopfbereich ohne Kachel – derselbe Aufbau wie in der Übersicht der Nachweise; Knopf und
          Menü stehen nebeneinander auf der Höhe des Statusstrahls. */}
      <SeitenKopf
        aktionen={
          canEdit &&
          (knopfAktion || !evidence.archiviert) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {knopfAktion && (
                // `Button` steckt in einer Hülle mit `inline-block`; erst diese Spalte zieht
                // sie auf die Breite, die das Menü daneben übrig lässt.
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                  <Button
                    variant="primary"
                    fullWidth
                    // Nie zweizeilig – lieber wird der Block breiter.
                    sx={{ whiteSpace: "nowrap" }}
                    onClick={() => onOeffneAktion(knopfAktion)}
                  >
                    {knopfAktion.label}
                  </Button>
                </Box>
              )}
              {!evidence.archiviert && (
                <ActionsMenuButton size="medium" onOpen={onOeffneMenu} />
              )}
            </Box>
          )
        }
      >
        <NachweisStatusStrahl evidence={evidence} />
      </SeitenKopf>

      <Section title="Nachweis" noOuterPadding>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputField
              label="Bezeichnung"
              value={entwurf.bezeichnung}
              onChange={(wert) => setze("bezeichnung", wert)}
              onBlur={(wert) => speichern({ bezeichnung: wert.trim() || null })}
              readOnly={!canEdit}
              placeholder="z.B. Schallimmissionsprognose Hohe Mark"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Dropdown
              label="Nachweisart"
              value={evidence.evidence_type_id ?? ""}
              options={evidenceTypeDropdownOptions}
              onChange={(wert) => speichern({ evidence_type_id: wert })}
              disabled={!canEdit}
              variant="floating"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            {/* Der Weg wechselt über eine Aktion: aus „Offen“ sofort, danach mit Rückfrage,
                weil die bisherigen Schritte entfallen. */}
            <Dropdown
              label="Bearbeitungsweg"
              value={evidence.processing_path}
              options={pathOptions}
              onChange={(wert) => onChangePath(wert as ProcessingPath)}
              disabled={!canEdit || evidence.archiviert}
              variant="floating"
              size="small"
              tooltip={currentPath && processingPathTooltip(currentPath)}
            />
          </Grid>
          {isCommission && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  label="Gutachter"
                  value={entwurf.gutachter}
                  options={experts.map((e) => e.name)}
                  onChange={(wert) => setze("gutachter", wert)}
                  onBlur={(wert) =>
                    saveNameWithEmail("gutachter", "expert_email", wert)
                  }
                  readOnly={!canEdit}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InputField
                  label="E-Mail des Gutachters"
                  value={entwurf.expert_email}
                  onChange={(wert) => setze("expert_email", wert)}
                  onBlur={(wert) =>
                    speichern({ expert_email: wert.trim() || null })
                  }
                  readOnly={!canEdit}
                  size="small"
                />
              </Grid>
            </>
          )}
          {isRequest && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Dropdown
                  label="Anfordern von"
                  value={side}
                  options={REQUESTER_SIDES}
                  onChange={(wert) => setSide(wert as "internal" | "external")}
                  disabled={!canEdit}
                  variant="floating"
                  size="small"
                />
              </Grid>
              {side === "internal" ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Dropdown
                    label="Kollegin oder Kollege"
                    value={evidence.requested_from_user_id ?? ""}
                    options={userOptions}
                    onChange={(wert) =>
                      speichern({ requested_from_user_id: wert || null })
                    }
                    disabled={!canEdit}
                    variant="floating"
                    size="small"
                  />
                </Grid>
              ) : (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Autocomplete
                      label="Institution"
                      value={entwurf.institution}
                      options={institutions.map((i) => i.name)}
                      onChange={(wert) => setze("institution", wert)}
                      onBlur={(wert) =>
                        saveNameWithEmail(
                          "institution",
                          "requested_from_email",
                          wert,
                        )
                      }
                      readOnly={!canEdit}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InputField
                      label="E-Mail der Institution"
                      value={entwurf.requested_from_email}
                      onChange={(wert) => setze("requested_from_email", wert)}
                      onBlur={(wert) =>
                        speichern({ requested_from_email: wert.trim() || null })
                      }
                      readOnly={!canEdit}
                      size="small"
                    />
                  </Grid>
                </>
              )}
            </>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Dropdown
              label="Verantwortlich"
              value={evidence.verantwortlich_id ?? ""}
              options={userOptions}
              onChange={(wert) =>
                speichern({ verantwortlich_id: wert || null })
              }
              disabled={!canEdit}
              variant="floating"
              size="small"
            />
          </Grid>
          {angaben.map((feld) => (
            <Grid size={{ xs: 12, sm: 6 }} key={feld.name}>
              {datumsfeld(feld)}
            </Grid>
          ))}
          {/* Gespeichert wird beim Schließen der Liste, nicht je Häkchen (ADR 004). Ein Baustein,
              der noch nicht im Antrag ist, kommt mit diesem Nachweis hinein. */}
          <Grid size={{ xs: 12 }}>
            <MultiSelectDropdown
              label="Antragsbausteine"
              values={bausteine}
              options={bausteinOptionen}
              onChange={setBausteine}
              onClose={(werte) =>
                speichern({ application_component_type_ids: werte })
              }
              disabled={!canEdit}
              size="small"
              searchable
              zeilenweise
            />
          </Grid>
          {/* Über die volle Breite: mehrere Dokumente stehen als Chips nebeneinander. Jede
              Änderung ist eine abgeschlossene Aktion und wird gleich gespeichert. */}
          <Grid size={{ xs: 12 }}>
            <DokumentListe
              label="Dokumente"
              dokumente={entwurf.dokumente}
              onChange={(dokumente) => {
                setze("dokumente", dokumente);
                speichern({ dokumente });
              }}
              readOnly={!canEdit}
            />
          </Grid>
        </Grid>
      </Section>

      {/* Der aktuelle Stand in freiem Text – etwa eine offene Rückfrage. Steht auch an den
          Terminen des Nachweises. */}
      <Section
        title={evidence.problem ? "Notiz · Problem" : "Notiz"}
        noOuterPadding
      >
        <InputFieldMultiline
          label="Notiz"
          value={entwurf.note}
          onChange={(wert) => setze("note", wert)}
          onBlur={(wert) => speichern({ note: wert.trim() ? wert : null })}
          readOnly={!canEdit}
          placeholder="Was gerade offen ist, z. B. eine Rückfrage an den Ersteller"
          minRows={4}
          maxRows={16}
        />
      </Section>

      {renderDeadlinesSection?.({ fieldRows, onFieldRowClick: oeffneFrist })}

      {costAccess !== "none" && (
        <Section title="Kosten" noOuterPadding>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <InputFieldNumber
                label="Geplante Kosten"
                value={betragAlsFeldwert(costItem?.betrag_geplant)}
                onBlur={(val) => onCostSave({ betrag_geplant: alsBetrag(val) })}
                readOnly={!canEdit || costAccess !== "write"}
                suffix="€"
                allowDecimals={false}
                variant="floating"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <InputFieldNumber
                label="Bisher angefallene Kosten"
                value={betragAlsFeldwert(costItem?.betrag_ist)}
                onBlur={(val) => onCostSave({ betrag_ist: alsBetrag(val) })}
                readOnly={!canEdit || costAccess !== "write"}
                suffix="€"
                allowDecimals={false}
                variant="floating"
              />
            </Grid>
          </Grid>
        </Section>
      )}

      <DialogTemplate
        open={!!offeneFrist}
        onClose={() => setOffeneFrist(null)}
        title={offeneFrist?.feld.label ?? ""}
        maxWidth="xs"
        actions={
          <>
            <Button variant="secondary" onClick={() => setOffeneFrist(null)}>
              Abbrechen
            </Button>
            <Button variant="primary" onClick={fristSpeichern}>
              Speichern
            </Button>
          </>
        }
      >
        {offeneFrist && (
          <Box sx={{ pt: 1 }}>
            <InputFieldDate
              label={offeneFrist.feld.label}
              value={offeneFrist.wert}
              onChange={(wert) =>
                setOffeneFrist({
                  ...offeneFrist,
                  wert: wert ?? "",
                  fehler: undefined,
                })
              }
              variant="floating"
              size="small"
              dateFormat="dd.MM.yyyy"
              error={offeneFrist.fehler}
            />
          </Box>
        )}
      </DialogTemplate>

      <Section title="Historie" noOuterPadding>
        <EvidenceHistorie
          key={historyKey}
          source={evidence}
          ladeHistorie={() => ladeHistorie(evidence)}
        />
      </Section>
    </Box>
  );
};

export default EvidenceDetail;
